// Render per-corner damper-edit provenance as chips on the Results pane.
//
// Fetched from GET /api/runs/{id}/artifacts/damper_setting_provenance
// when the Run had overlay edits. Baseline Runs (R0 + Re-run baseline
// retries) have no such artifact — this component returns null in
// that case so it stays out of the UI for unedited Runs.
//
// Each edited corner gets a chip showing pvp_match_mode + confidence + the
// damper F-V source kind (raw measured / derived measured / fallback) and the
// redacted curve_id. The raw .pvp path is never sent to the client (Phase 3
// redaction); engineers inspect the full artifact via the network tab.
import { useEffect, useState } from "react";

type CornerProvenance = {
  pvp_match_mode: string;
  confidence: number;
  click_distance: number | null;
  // Phase 3: redacted public curve key + resolved F-V source kind. No path.
  curve_id: string | null;
  damper_fv_source: string | null;
  fallback_reason: string | null;
};

type DamperSettingProvenanceArtifact = {
  schema_version: number;
  match_context: string;
  corners: Record<string, CornerProvenance>;
};

function modeTone(mode: string): { color: string; label: string } {
  switch (mode) {
    case "click_exact":
      return { color: "#7ed47e", label: "EXACT" };
    case "click_nearest":
      return { color: "#bfd47e", label: "NEAR" };
    case "code_only":
      return { color: "#e4c47a", label: "CODE-ONLY" };
    case "fallback_no_match":
      return { color: "#ff9a9a", label: "FALLBACK" };
    case "baseline_event_context":
      return { color: "#7eb2d4", label: "BASELINE" };
    default:
      return { color: "#aaa", label: mode };
  }
}

// Honest F-V source label: distinguishes a raw bench measurement from the
// derived (precomputed, bit-identical) catalog curve from a fallback curve.
function sourceLabel(source: string | null): string | null {
  switch (source) {
    case "raw_pvp":
      return "raw measured";
    case "derived_catalog":
      return "derived measured";
    case null:
    case undefined:
      return null;
    default:
      return source;
  }
}

export type DamperSettingProvenanceChipsProps = {
  runId: number;
};

export function DamperSettingProvenanceChips({
  runId,
}: DamperSettingProvenanceChipsProps) {
  const [artifact, setArtifact] = useState<DamperSettingProvenanceArtifact | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setArtifact(null);
    setMissing(false);
    fetch(`/api/runs/${runId}/artifacts/damper_setting_provenance`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 404) {
          setMissing(true);
          return;
        }
        if (!r.ok) {
          setMissing(true);
          return;
        }
        const parsed = (await r.json()) as DamperSettingProvenanceArtifact;
        if (!cancelled) setArtifact(parsed);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (missing || artifact === null) return null;
  const entries = Object.entries(artifact.corners);
  if (entries.length === 0) return null;

  return (
    <div data-testid="damper-prov-chips" style={{ marginTop: "12px" }}>
      <div className="muted" style={{ fontSize: "11px", marginBottom: "4px" }}>
        Damper-edit provenance (edited corners only):
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {entries.map(([corner, prov]) => {
          const tone = modeTone(prov.pvp_match_mode);
          const src = sourceLabel(prov.damper_fv_source);
          const tooltipParts = [
            `mode: ${prov.pvp_match_mode}`,
            `confidence: ${prov.confidence.toFixed(2)}`,
            prov.click_distance !== null && `click_distance: ${prov.click_distance}`,
            src && `F-V source: ${src}`,
            prov.curve_id && `curve: ${prov.curve_id}`,
            prov.fallback_reason && `reason: ${prov.fallback_reason}`,
          ].filter(Boolean) as string[];
          return (
            <span
              key={corner}
              data-testid={`damper-prov-${corner}`}
              title={tooltipParts.join("\n")}
              style={{
                padding: "3px 8px",
                borderRadius: "3px",
                backgroundColor: "#222",
                border: `1px solid ${tone.color}`,
                fontSize: "11px",
              }}
            >
              <strong style={{ color: tone.color, marginRight: "6px" }}>
                {corner.toUpperCase()}
              </strong>
              <span style={{ color: tone.color }}>{tone.label}</span>
              <span className="muted" style={{ marginLeft: "6px" }}>
                {prov.confidence.toFixed(2)}
              </span>
              {src && (
                <span
                  className="muted"
                  data-testid={`damper-prov-${corner}-source`}
                  style={{ marginLeft: "6px", fontStyle: "italic" }}
                >
                  {src}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
